import { useCallback, useEffect, useRef, useState } from "react"

import type { EditSession } from "./edit-session"

export interface EditSessionSwitchOptions<TKey, TRecord> {
  /** The item edited first. */
  readonly initialKey: TKey
  /**
   * Loads the record of an item: the screen's own query or command. The signal aborts when the item is no longer
   * current or the screen unmounts; a record that arrives after that is dropped.
   */
  readonly load: (key: TKey, context: { readonly signal: AbortSignal }) => Promise<TRecord>
}

export interface EditSessionSwitch<TKey, TRecord> {
  /** The item being edited or loaded. Pass it to `useEditSession` as `recordKey`. */
  readonly key: TKey
  /** The record of `key` once it has loaded; undefined while it loads or when its load failed. */
  readonly record: TRecord | undefined
  /** Why the record of `key` did not load, or undefined. */
  readonly error: unknown
  /** Loads the record of `key` again, after an error. */
  retry(): void
  /**
   * Moves the editor to another item. When `session` has unsaved changes, it first asks as leaving does
   * (`session.requestLeave()`): Save moves only after a successful save, Discard moves, Cancel stays. Resolves whether
   * `key` is the current item afterwards, so a list can put its selection back. A request while another one waits
   * for the user resolves false.
   */
  request(key: TKey, session: Pick<EditSession<object>, "requestLeave">): Promise<boolean>
}

type LoadState<TKey, TRecord> =
  | { readonly key: TKey; readonly attempt: number; readonly status: "loaded"; readonly record: TRecord }
  | { readonly key: TKey; readonly attempt: number; readonly status: "failed"; readonly error: unknown }

/**
 * The current item of a list-plus-editor screen, whose editor keeps one edit session for item after item:
 *
 * ```tsx
 * const item = useEditSessionSwitch({ initialKey: firstId, load: (id, { signal }) => loadItem(id, signal) })
 * const session = useEditSession({ recordKey: item.key, value: item.record?.values, revision: item.record?.revision, save })
 * // In the list: if (!(await item.request(rowId, session))) restore the list's selection.
 * ```
 *
 * The record of the current key is the only one it hands out: a load for an item that is no longer current never
 * arrives, so the session never takes another item's record as its baseline.
 */
export function useEditSessionSwitch<TKey, TRecord>(
  options: EditSessionSwitchOptions<TKey, TRecord>
): EditSessionSwitch<TKey, TRecord> {
  const [key, setKey] = useState(options.initialKey)
  const [attempt, setAttempt] = useState(0)
  const [loaded, setLoaded] = useState<LoadState<TKey, TRecord> | null>(null)
  const loadRef = useRef(options.load)
  loadRef.current = options.load
  const keyRef = useRef(key)
  keyRef.current = key
  const requestingRef = useRef(false)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    void Promise.resolve()
      .then(() => {
        signal.throwIfAborted()
        return loadRef.current(key, { signal })
      })
      .then(
        (record) => {
          if (!signal.aborted) setLoaded({ key, attempt, status: "loaded", record })
        },
        (error: unknown) => {
          if (!signal.aborted) setLoaded({ key, attempt, status: "failed", error })
        }
      )
    return () => controller.abort()
  }, [attempt, key])

  const request = useCallback(async (next: TKey, session: Pick<EditSession<object>, "requestLeave">) => {
    if (Object.is(next, keyRef.current)) return true
    if (requestingRef.current) return false
    requestingRef.current = true
    try {
      if (!(await session.requestLeave())) return false
      keyRef.current = next
      setLoaded(null)
      setKey(next)
      return true
    } finally {
      requestingRef.current = false
    }
  }, [])
  const retry = useCallback(() => setAttempt((value) => value + 1), [])

  const current = loaded != null && Object.is(loaded.key, key) && loaded.attempt === attempt ? loaded : null
  return {
    key,
    record: current?.status === "loaded" ? current.record : undefined,
    error: current?.status === "failed" ? current.error : undefined,
    retry,
    request,
  }
}
